import {
  Business as BusinessIcon,
  CallMerge as CallMergeIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  Place as PlaceIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material'
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import type { ContactCompany, ContactCompanyOwnerId } from '@otw/models/leads/contactCompanies'
import React, { useEffect, useMemo, useState } from 'react'
import type { MergeContactCompaniesFieldSources } from '@/hooks/contacts/useContactCompanies'
import { useUsers } from 'hooks/useUsers'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import {
  formatPickupLocationAddress,
  resolvePickupLocationById,
} from '../../util/pickupLocationDisplay'
import { resolveOwnerDisplayName } from '../../util/resolveOwnerDisplayName'
import {
  getDefaultMergeFieldSources,
  mergeCompanyMembers,
  type MergeContactCompaniesFieldKey,
  type MergeFieldSource,
} from '../../util/mergeContactCompanies'

interface MergeCompaniesDialogProps {
  open: boolean
  onClose: () => void
  companies: ContactCompany[]
  /** Pre-selected company ids from the grid (0–2). */
  initialPrimaryId?: string | null
  initialSecondaryId?: string | null
  onMerge: (input: {
    primaryCompanyId: string
    secondaryCompanyId: string
    fieldSources: MergeContactCompaniesFieldSources
  }) => Promise<void>
  isMerging?: boolean
}

type MergeFieldConfig = {
  key: MergeContactCompaniesFieldKey
  label: string
  format: (company: ContactCompany) => string
}

const getOwnerLabel = (
  ownerId: ContactCompanyOwnerId,
  users: Parameters<typeof resolveOwnerDisplayName>[1],
): string => resolveOwnerDisplayName(ownerId, users, `Owner #${ownerId}`)

const formatOptional = (value: string | undefined): string => {
  const trimmed = value?.trim()
  return trimmed || '—'
}

const MergeCompaniesDialog: React.FC<MergeCompaniesDialogProps> = ({
  open,
  onClose,
  companies,
  initialPrimaryId = null,
  initialSecondaryId = null,
  onMerge,
  isMerging = false,
}) => {
  const theme = useTheme()
  const { pickupLocations } = usePickupLocations()
  const { users: allUsers } = useUsers()

  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [companies],
  )

  const [primaryCompany, setPrimaryCompany] = useState<ContactCompany | null>(null)
  const [secondaryCompany, setSecondaryCompany] = useState<ContactCompany | null>(null)
  const [fieldSources, setFieldSources] = useState<MergeContactCompaniesFieldSources | null>(
    null,
  )

  useEffect(() => {
    if (!open) return
    const primary = initialPrimaryId
      ? sortedCompanies.find((c) => c.id === initialPrimaryId) ?? null
      : null
    const secondary = initialSecondaryId
      ? sortedCompanies.find((c) => c.id === initialSecondaryId) ?? null
      : null
    setPrimaryCompany(primary)
    setSecondaryCompany(secondary)
  }, [open, initialPrimaryId, initialSecondaryId, sortedCompanies])

  useEffect(() => {
    if (!primaryCompany || !secondaryCompany) {
      setFieldSources(null)
      return
    }
    if (primaryCompany.id === secondaryCompany.id) {
      setFieldSources(null)
      return
    }
    setFieldSources(getDefaultMergeFieldSources(primaryCompany, secondaryCompany))
  }, [primaryCompany, secondaryCompany])

  const formatAddress = (locationId: string | null | undefined): string => {
    const location = resolvePickupLocationById(pickupLocations, locationId)
    return formatPickupLocationAddress(location) || '—'
  }

  const fieldConfigs = useMemo<MergeFieldConfig[]>(
    () => [
      { key: 'name', label: 'Company name', format: (c) => formatOptional(c.name) },
      { key: 'website', label: 'Website', format: (c) => formatOptional(c.website) },
      { key: 'notes', label: 'Notes', format: (c) => formatOptional(c.notes) },
      {
        key: 'ownerId',
        label: 'Company owner',
        format: (c) => getOwnerLabel(c.ownerId, allUsers),
      },
      {
        key: 'businessAddressId',
        label: 'Business address',
        format: (c) => formatAddress(c.businessAddressId),
      },
      {
        key: 'dropoffLocationIds',
        label: 'Dropoff addresses',
        format: (c) => {
          const labels = (c.dropoffLocationIds ?? [])
            .map((id) => formatAddress(id))
            .filter((label) => label !== '—')
          return labels.length > 0 ? labels.join('; ') : '—'
        },
      },
    ],
    [pickupLocations, allUsers],
  )

  const mergePreview = useMemo(() => {
    if (!primaryCompany || !secondaryCompany || primaryCompany.id === secondaryCompany.id) {
      return null
    }
    const mergedMembers = mergeCompanyMembers(
      primaryCompany.members,
      secondaryCompany.members,
    )
    const activityCount =
      (primaryCompany.activities?.length ?? 0) + (secondaryCompany.activities?.length ?? 0)
    return {
      memberCount: mergedMembers.length,
      activityCount,
    }
  }, [primaryCompany, secondaryCompany])

  const canMerge =
    Boolean(primaryCompany && secondaryCompany) &&
    primaryCompany!.id !== secondaryCompany!.id &&
    fieldSources != null

  const handleFieldSourceChange = (key: MergeContactCompaniesFieldKey, value: string) => {
    if (!fieldSources) return
    setFieldSources({
      ...fieldSources,
      [key]: value as MergeFieldSource,
    })
  }

  const handleMerge = async () => {
    if (!primaryCompany || !secondaryCompany || !fieldSources) return
    await onMerge({
      primaryCompanyId: primaryCompany.id,
      secondaryCompanyId: secondaryCompany.id,
      fieldSources,
    })
  }

  const primaryOptions = sortedCompanies.filter((c) => c.id !== secondaryCompany?.id)
  const secondaryOptions = sortedCompanies.filter((c) => c.id !== primaryCompany?.id)

  const renderValueCell = (
    company: ContactCompany,
    value: string,
    selected: boolean,
    side: MergeFieldSource,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        flex: 1,
        minWidth: 0,
        borderRadius: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <FormControlLabel
        value={side}
        control={<Radio size="small" />}
        label={
          <Typography
            variant="body2"
            sx={{
              wordBreak: 'break-word',
              fontWeight: selected ? 600 : 400,
              color: selected ? 'text.primary' : 'text.secondary',
            }}
          >
            {value}
          </Typography>
        }
        sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
      />
    </Paper>
  )

  return (
    <Dialog open={open} onClose={() => !isMerging && onClose()} maxWidth="md" fullWidth>
      <Box
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'primary.contrastText',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CallMergeIcon />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Merge companies
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Combine duplicate records and choose which details to keep.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            disabled={isMerging}
            size="small"
            aria-label="Close"
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ bgcolor: 'background.default' }}>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Select companies
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                options={primaryOptions}
                value={primaryCompany}
                onChange={(_, value) => setPrimaryCompany(value)}
                getOptionLabel={(option) => option.name || 'Unnamed company'}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={isMerging}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Keep this record (primary)"
                    size="small"
                    helperText="This company stays in the system after the merge."
                  />
                )}
              />
              <Autocomplete
                options={secondaryOptions}
                value={secondaryCompany}
                onChange={(_, value) => setSecondaryCompany(value)}
                getOptionLabel={(option) => option.name || 'Unnamed company'}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={isMerging}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Merge into primary (duplicate)"
                    size="small"
                    helperText="This record will be removed after its data is combined."
                  />
                )}
              />
            </Stack>
          </Paper>

          {primaryCompany && secondaryCompany && primaryCompany.id === secondaryCompany.id && (
            <Alert severity="warning">Choose two different companies.</Alert>
          )}

          {canMerge && mergePreview && fieldSources && (
            <>
              <Alert
                severity="info"
                icon={<WarningIcon />}
                sx={{ '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {secondaryCompany!.name} will be deleted after merging into{' '}
                  {primaryCompany!.name}.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    icon={<GroupIcon />}
                    label={`${mergePreview.memberCount} linked contact${
                      mergePreview.memberCount === 1 ? '' : 's'
                    }`}
                  />
                  <Chip
                    size="small"
                    icon={<HistoryIcon />}
                    label={`${mergePreview.activityCount} activit${
                      mergePreview.activityCount === 1 ? 'y' : 'ies'
                    } combined`}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Contacts and leads that reference the duplicate company name will be updated to
                  the merged name.
                </Typography>
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Choose values for the merged company
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2, display: { xs: 'none', sm: 'flex' } }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      size="small"
                      icon={<BusinessIcon />}
                      label={primaryCompany!.name}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      size="small"
                      icon={<BusinessIcon />}
                      label={secondaryCompany!.name}
                      variant="outlined"
                    />
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  {fieldConfigs.map((field) => {
                    const primaryValue = field.format(primaryCompany!)
                    const secondaryValue = field.format(secondaryCompany!)
                    const source = fieldSources[field.key]
                    const valuesDiffer =
                      primaryValue.trim().toLowerCase() !==
                      secondaryValue.trim().toLowerCase()

                    return (
                      <Box key={field.key}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 0.75 }}
                        >
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            {field.label.toUpperCase()}
                          </Typography>
                          {!valuesDiffer && primaryValue !== '—' && (
                            <Chip label="Same" size="small" sx={{ height: 18, fontSize: 11 }} />
                          )}
                        </Stack>
                        <RadioGroup
                          row
                          value={source}
                          onChange={(e) =>
                            handleFieldSourceChange(field.key, e.target.value)
                          }
                          sx={{ gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                        >
                          {renderValueCell(
                            primaryCompany!,
                            primaryValue,
                            source === 'primary',
                            'primary',
                          )}
                          {renderValueCell(
                            secondaryCompany!,
                            secondaryValue,
                            source === 'secondary',
                            'secondary',
                          )}
                        </RadioGroup>
                      </Box>
                    )
                  })}
                </Stack>
              </Paper>

              {fieldSources.name === 'secondary' && (
                <Alert severity="warning" icon={<PlaceIcon />}>
                  The surviving company record will use the duplicate&apos;s name. Linked
                  contacts and leads will be updated to match.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isMerging}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CallMergeIcon />}
          disabled={!canMerge || isMerging}
          onClick={() => void handleMerge()}
        >
          {isMerging ? 'Merging…' : 'Merge companies'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MergeCompaniesDialog
